import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { ErrorOutline, Refresh } from "@mui/icons-material";
import { notifyDiscord } from "../utils/discordNotifier";

type Props = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
};

type State = {
  hasError: boolean;
  error?: Error;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    void notifyDiscord({
      title: "Frontend — uncaught React render error",
      context: {
        pageUrl: window.location.href,
        componentStack: info.componentStack,
      },
      reason: error.stack ?? error.message,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          minHeight={300}
          gap={2}
          p={4}
          role="alert"
          aria-live="assertive"
        >
          <ErrorOutline color="error" sx={{ fontSize: 56 }} />
          <Typography variant="h6" color="error" fontWeight="bold">
            Bir hata oluştu
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            maxWidth={400}
          >
            {this.state.error?.message ||
              "Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin."}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={this.handleReset}
          >
            Tekrar Dene
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}
